import { useState, useEffect } from 'react'
import { useGameStore } from '@store/gameStore'
import './HomeScreenScreen.css'

// App icon configuration
const APP_ICONS = [
    { id: 'messages', emoji: '💬', name: 'Nachrichten', color: '#34c759' },
    { id: 'phone', emoji: '📞', name: 'Telefon', color: '#34c759' },
    { id: 'safari', emoji: '🧭', name: 'Safari', color: '#007aff' },
    { id: 'mail', emoji: '📧', name: 'Mail', color: '#007aff' },
    { id: 'music', emoji: '🎵', name: 'Musik', color: '#fc3c44' },
    { id: 'photos', emoji: '🌈', name: 'Fotos', color: '#fff' },
    { id: 'camera', emoji: '📷', name: 'Kamera', color: '#8e8e93' },
    { id: 'settings', emoji: '⚙️', name: 'Einstellungen', color: '#8e8e93' },
    { id: 'appstore', emoji: '🅰️', name: 'App Store', color: '#007aff' },
    { id: 'calendar', emoji: '📅', name: 'Kalender', color: '#ff3b30' },
    { id: 'clock', emoji: '🕐', name: 'Uhr', color: '#000' },
    { id: 'weather', emoji: '⛅', name: 'Wetter', color: '#5ac8fa' },
    { id: 'maps', emoji: '🗺️', name: 'Karten', color: '#34c759' },
    { id: 'notes', emoji: '📝', name: 'Notizen', color: '#ffcc00' },
    { id: 'pay2lose', emoji: '', name: 'Pay2Lose', color: '#000', isTarget: true },
    { id: 'health', emoji: '❤️', name: 'Health', color: '#ff2d55' },
    { id: 'wallet', emoji: '💳', name: 'Wallet', color: '#000' },
    { id: 'files', emoji: '📁', name: 'Dateien', color: '#007aff' },
    { id: 'reminders', emoji: '📋', name: 'Erinnerungen', color: '#007aff' },
    { id: 'news', emoji: '📰', name: 'News', color: '#fc3c44' },
]

const DOCK_APPS = [
    { id: 'phone', emoji: '📞', name: 'Telefon', color: '#34c759' },
    { id: 'safari', emoji: '🧭', name: 'Safari', color: '#007aff' },
    { id: 'messages', emoji: '💬', name: 'Nachrichten', color: '#34c759' },
    { id: 'music', emoji: '🎵', name: 'Musik', color: '#fc3c44' },
]

export function HomeScreenScreen() {
    const setScreen = useGameStore(state => state.setScreen)
    const [isLaunching, setIsLaunching] = useState(false)
    const [highlightApp, setHighlightApp] = useState(false)
    const [launchPosition, setLaunchPosition] = useState({ x: 0, y: 0 })

    // Get current time for status bar
    const currentTime = new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
    })

    // Animation timeline
    useEffect(() => {
        // Phase 1: After 2 seconds, highlight Pay2Lose app
        const highlightTimer = setTimeout(() => {
            setHighlightApp(true)
        }, 2000)

        // Phase 2: After 3 seconds total, launch the app
        const launchTimer = setTimeout(() => {
            // Get the position of the Pay2Lose app icon
            const appIcon = document.querySelector('.home-screen__app--target')
            if (appIcon) {
                const rect = appIcon.getBoundingClientRect()
                setLaunchPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2
                })
            }
            setIsLaunching(true)
        }, 3000)

        // Phase 3: Transition to setup screen
        const transitionTimer = setTimeout(() => {
            setScreen('setup')
        }, 3700)

        return () => {
            clearTimeout(highlightTimer)
            clearTimeout(launchTimer)
            clearTimeout(transitionTimer)
        }
    }, [setScreen])

    return (
        <div className="home-screen">
            {/* iOS-style Status Bar */}
            <div className="home-screen__status-bar">
                <span className="home-screen__time">{currentTime}</span>
                <div className="home-screen__notch"></div>
                <div className="home-screen__status-icons">
                    <span>📶</span>
                    <span>📡</span>
                    <span>🔋</span>
                </div>
            </div>

            {/* App Grid */}
            <div className="home-screen__grid">
                {APP_ICONS.map(app => (
                    <div
                        key={app.id}
                        className={`home-screen__app ${app.isTarget ? 'home-screen__app--target' : ''} ${app.isTarget && highlightApp ? 'home-screen__app--highlight' : ''}`}
                    >
                        <div className="home-screen__app-icon" style={{ background: app.color }}>
                            {app.isTarget ? (
                                <img src="./appicon.png" alt="Pay2Lose" className="home-screen__app-img" />
                            ) : (
                                <span className="home-screen__app-emoji">{app.emoji}</span>
                            )}
                        </div>
                        <span className="home-screen__app-name">{app.name}</span>
                    </div>
                ))}
            </div>

            {/* Page Indicator Dots */}
            <div className="home-screen__page-dots">
                <span className="home-screen__dot home-screen__dot--active"></span>
                <span className="home-screen__dot"></span>
                <span className="home-screen__dot"></span>
            </div>

            {/* Dock */}
            <div className="home-screen__dock">
                {DOCK_APPS.map(app => (
                    <div key={app.id} className="home-screen__dock-app">
                        <div className="home-screen__app-icon" style={{ background: app.color }}>
                            <span className="home-screen__app-emoji">{app.emoji}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Home Indicator */}
            <div className="home-screen__home-indicator"></div>

            {/* App Launch Overlay */}
            {isLaunching && (
                <div
                    className="home-screen__launch-overlay"
                    style={{
                        '--launch-x': `${launchPosition.x}px`,
                        '--launch-y': `${launchPosition.y}px`
                    } as React.CSSProperties}
                >
                    <div className="home-screen__launch-icon">
                        <img src="./appicon.png" alt="Pay2Lose" />
                    </div>
                </div>
            )}
        </div>
    )
}
